<template>
    <section class="cta-section">
        <div class="container">
            <div class="cta-content">
                <!-- Logo -->
                <div class="cta-logo">
                    <div class="logo-icon logo-image">
                        <img :src="logoUrl" alt="微鲤AI logo" class="logo-image" loading="lazy" />
                    </div>
                </div>

                <!-- Main Content -->
                <div class="cta-header">
                    <h2 class="cta-title">{{ title }}</h2>
                    <p class="cta-description">{{ description }}</p>
                </div>

                <!-- CTA Button -->
                <div class="cta-actions">
                    <button @click="handleClick" class="cta-primary-btn">
                        {{ buttonText }}
                    </button>

                    <p v-if="disclaimer" class="cta-disclaimer">
                        {{ disclaimer }}
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
interface Props {
    title: string
    description: string
    buttonText: string
    disclaimer?: string
    logoUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
    logoUrl: 'https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp',
    disclaimer: '',
})

const emit = defineEmits<{
    click: []
}>()

const handleClick = () => {
    emit('click')
}
</script>

<style lang="less" scoped>
.cta-section {
    padding: 6rem 0;
    background: linear-gradient(180deg,
            #000 0%,
            rgba(0, 0, 0, 0) 10%,
            rgba(0, 0, 0, 0) 100%),
        no-repeat;
    background-size: cover;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 30% 20%,
                rgba(59, 130, 246, 0.1) 0%,
                transparent 50%),
            radial-gradient(circle at 70% 80%,
                rgba(139, 92, 246, 0.1) 0%,
                transparent 50%);
        pointer-events: none;
    }
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.cta-content {
    text-align: center;
    position: relative;
    z-index: 1;
}

.cta-logo {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
}

.logo-icon {
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &.logo-image {
        width: 6.25rem;
        height: 6.25rem;
    }

    .logo-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.cta-header {
    margin-bottom: 2rem;
}

.cta-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    color: #f5f2f0;
    line-height: 1.2;
    background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.cta-description {
    font-size: 1.25rem;
    color: #adadad;
    margin-bottom: 0;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.cta-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
}

.cta-primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 6rem;
    border: 2px solid var(--active-btn);
    background: rgba(12, 36, 0, 0.4);
    color: white;
    text-decoration: none;
    border-radius: 0.75rem;
    font-size: 1.125rem;
    font-weight: 600;
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    z-index: 1;

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 0.75rem;
        z-index: -1;
        opacity: 0.3;
        filter: blur(8px);
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(153, 247, 104, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}

.cta-disclaimer {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 400;
}

@media (max-width: 768px) {
    .cta-section {
        padding: 4rem 0;
    }

    .cta-title {
        font-size: 2rem;
    }

    .cta-description {
        font-size: 1rem;
        padding: 0 1rem;
    }

    .cta-primary-btn {
        padding: 1rem 3rem;
        font-size: 1rem;
    }
}

@media (max-width: 480px) {
    .cta-title {
        font-size: 1.5rem;
    }

    .cta-description {
        font-size: 0.875rem;
    }

    .cta-primary-btn {
        padding: 0.875rem 2rem;
        font-size: 0.9rem;
    }

    .cta-disclaimer {
        font-size: 0.8rem;
    }

    .logo-icon.logo-image {
        width: 4rem;
        height: 4rem;
    }
}
</style>
